<script>
  // The top view (Miles 2026-11-03): one toggle, Offers | Doors, the same routes seen from either side.
  // Offers: tap one → every way to get to it (Tech screens and IRL steps), the shortest marked "optimal".
  // Doors: tap one → every offer it leads to. Walk a route here: Open each address, tick each step tested.
  import { workspace } from '../lib/stores/workspace.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';
  import { techPath, reaches, irlSteps, openable } from '../lib/routes.js';
  import RouteSteps from './routes/RouteSteps.svelte';

  const MODE_KEY = 'bb_top_view';
  let mode = $state((() => { try { return localStorage.getItem(MODE_KEY) || 'offers'; } catch { return 'offers'; } })());
  let journeys = $state([]), pages = $state([]), choices = $state([]);
  let openId = $state(null), openRoute = $state(null), q = $state('');

  function setMode(m) { mode = m; openId = null; openRoute = null; try { localStorage.setItem(MODE_KEY, m); } catch {} }

  $effect(() => { if (workspace.id) loadTech(workspace.id); });

  async function loadTech(ws) {
    const [j, p, c] = await Promise.all([
      supabase.from('bb_tech_journeys').select('*').eq('workspace_id', ws).is('deleted_at', null),
      supabase.from('bb_tech_pages').select('*').eq('workspace_id', ws).is('deleted_at', null),
      supabase.from('bb_tech_choices').select('*').eq('workspace_id', ws).is('deleted_at', null)
    ]);
    journeys = j.data || []; pages = p.data || []; choices = c.data || [];
  }

  const pagesById = $derived(new Map(pages.map(p => [p.id, p])));
  const stepsById = $derived(new Map((workspace.steps || []).map(s => [s.id, s])));
  const audName = (id) => workspace.audiences.find(a => a.id === id)?.name || null;
  const offerFor = (j) => workspace.deals.find(d => d.offer_id && d.offer_id === j.offer_id) || null;

  function techRoute(j, from) {
    const items = techPath(j, from, pagesById, choices);
    return { key: 't:' + j.id, tech: true, name: j.name, who: audName(j.audience_id), items,
      door: items.find(p => p.kind === 'door') || items[0] || null };
  }

  function routesFor(d) {
    const out = [
      ...journeys.filter(j => d.offer_id && j.offer_id === d.offer_id).map(j => techRoute(j)),
      ...(workspace.chains || []).filter(c => c.terminal_deal_id === d.id).map(c => ({
        key: 'i:' + c.id, tech: false, name: c.name || 'IRL route', who: audName(c.starting_audience_id),
        items: irlSteps(c, stepsById), door: null }))
    ];
    out.sort((a, b) => (a.items.length || 999) - (b.items.length || 999));
    return out;
  }

  const offers = $derived.by(() => {
    const term = q.trim().toLowerCase();
    return workspace.deals
      .filter(d => !term || (d.name || '').toLowerCase().includes(term))
      .map(d => ({ d, routes: routesFor(d) }))
      .sort((a, b) => (b.routes.length > 0) - (a.routes.length > 0) || (a.d.name || '').localeCompare(b.d.name || ''));
  });

  // A door is a screen marked Door or Sells it, or the first screen of a route.
  const doors = $derived.by(() => {
    const ids = new Set(pages.filter(p => p.kind === 'door' || p.kind === 'sells').map(p => p.id));
    journeys.forEach(j => j.starting_page_id && ids.add(j.starting_page_id));
    const term = q.trim().toLowerCase();
    return [...ids].map(id => pagesById.get(id)).filter(Boolean)
      .filter(p => !term || (p.title || '').toLowerCase().includes(term))
      .map(p => ({ p, leads: journeys.filter(j => reaches(j, p.id, choices)).map(j => ({ j, offer: offerFor(j), route: techRoute(j, p.id) })) }))
      .sort((a, b) => (a.p.title || '').localeCompare(b.p.title || ''));
  });

  const unlinked = $derived(journeys.filter(j => !j.offer_id));

  async function linkJourney(jId, d) {
    if (!jId || !d.offer_id) return;
    const { data, error } = await supabase.from('bb_tech_journeys').update({ offer_id: d.offer_id }).eq('id', jId).select('id');
    if (error || !data?.length) { showToast(error?.message || 'Not saved', 'error'); return; }
    journeys = journeys.map(j => (j.id === jId ? { ...j, offer_id: d.offer_id } : j));
  }

  function changed(tech, id, patch) {
    if (tech) pages = pages.map(p => (p.id === id ? { ...p, ...patch } : p));
    else { const s = workspace.steps.find(x => x.id === id); if (s) Object.assign(s, patch); }
  }

  function goOffer(d) { if (!d) return; setMode('offers'); openId = d.id; }
</script>

<div class="ov">
  <div class="ov-head">
    <div class="ov-toggle">
      <button type="button" class:on={mode === 'offers'} onclick={() => setMode('offers')}>Offers</button>
      <button type="button" class:on={mode === 'doors'} onclick={() => setMode('doors')}>Doors</button>
    </div>
    <input class="ov-q" placeholder={mode === 'offers' ? 'Find an offer' : 'Find a door'} bind:value={q} />
  </div>

  {#if mode === 'offers'}
    {#each offers as { d, routes } (d.id)}
      <div class="ov-card" class:open={openId === d.id}>
        <button type="button" class="ov-row" onclick={() => { openId = openId === d.id ? null : d.id; openRoute = null; }}>
          <span class="ov-name">{d.name}</span>
          {#if routes.some(r => r.tech)}<span class="chip tech">Tech</span>{/if}
          {#if routes.some(r => !r.tech)}<span class="chip irl">IRL</span>{/if}
          {#if !routes.length}<span class="ov-dim">No route yet</span>{/if}
        </button>
        {#if openId === d.id}
          <div class="ov-body">
            {#if d.offer?.promise_text}<p class="ov-promise">{d.offer.promise_text}</p>{/if}
            {#each routes as r, i (r.key)}
              <div class="ov-route">
                <button type="button" class="ov-rrow" onclick={() => openRoute = openRoute === r.key ? null : r.key}>
                  <span class="chip" class:tech={r.tech} class:irl={!r.tech}>{r.tech ? 'Tech' : 'IRL'}</span>
                  <span class="ov-rname">{r.name}{#if r.who}<span class="ov-dim"> · {r.who}</span>{/if}</span>
                  <span class="ov-dim">{r.items.length} {r.items.length === 1 ? 'step' : 'steps'}</span>
                  {#if i === 0 && r.items.length}<span class="chip best">Optimal</span>{/if}
                </button>
                {#if r.door?.url}<a class="ov-open" href={openable(r.door.url)} target="_blank" rel="noopener">Open door ↗</a>{/if}
                {#if openRoute === r.key}<RouteSteps items={r.items} tech={r.tech} onChanged={(id, patch) => changed(r.tech, id, patch)} />{/if}
              </div>
            {/each}
            {#if d.offer_id && unlinked.length}
              <select class="ov-link" value="" onchange={(e) => linkJourney(e.currentTarget.value, d)}>
                <option value="">+ Link a tech route to this offer</option>
                {#each unlinked as j (j.id)}<option value={j.id}>{j.name}</option>{/each}
              </select>
            {/if}
            {#if !routes.length}<p class="ov-dim">Make a route on Map: Tech Mode on for the screens, off for IRL steps.</p>{/if}
          </div>
        {/if}
      </div>
    {:else}
      <p class="ov-dim">No offers yet.</p>
    {/each}
  {:else}
    {#each doors as { p, leads } (p.id)}
      <div class="ov-card" class:open={openId === p.id}>
        <button type="button" class="ov-row" onclick={() => { openId = openId === p.id ? null : p.id; openRoute = null; }}>
          <span class="ov-name">{p.title}</span>
          <span class="chip door">{p.kind === 'sells' ? 'Sells it' : 'Door'}</span>
          {#if p.url}<span class="ov-dim">{p.url}</span>{/if}
        </button>
        {#if openId === p.id}
          <div class="ov-body">
            {#if p.url}<a class="ov-open" href={openable(p.url)} target="_blank" rel="noopener">Open ↗</a>{/if}
            {#each leads as { j, offer, route } (j.id)}
              <div class="ov-route">
                <button type="button" class="ov-rrow" onclick={() => openRoute = openRoute === j.id ? null : j.id}>
                  <span class="ov-rname">{offer?.name || j.name}{#if !offer}<span class="ov-dim"> · no offer linked</span>{/if}</span>
                  <span class="ov-dim">{route.items.length} {route.items.length === 1 ? 'step' : 'steps'}</span>
                </button>
                {#if offer}<button type="button" class="ov-go" onclick={() => goOffer(offer)}>See all routes →</button>{/if}
                {#if openRoute === j.id}<RouteSteps items={route.items} tech={true} onChanged={(id, patch) => changed(true, id, patch)} />{/if}
              </div>
            {:else}
              <p class="ov-dim">No route starts here yet.</p>
            {/each}
          </div>
        {/if}
      </div>
    {:else}
      <p class="ov-dim">No doors yet. Mark a screen as a Door inside a route.</p>
    {/each}
  {/if}
</div>

<style>
  .ov { max-width: 860px; margin: 0 auto; padding: 24px 16px 80px; display: flex; flex-direction: column; gap: 8px; }
  .ov-head { display: flex; gap: 12px; align-items: center; margin-bottom: 8px; flex-wrap: wrap; }
  .ov-toggle { display: inline-flex; border: 1px solid var(--border); border-radius: 999px; padding: 3px; }
  .ov-toggle button { background: transparent; border: none; color: var(--text-muted); padding: 6px 16px; border-radius: 999px; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
  .ov-toggle button.on { background: #d4af37; color: #141216; }
  .ov-q { flex: 1; min-width: 160px; background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.12); border-radius: 999px; color: var(--text); font: inherit; font-size: 13px; padding: 7px 14px; }
  .ov-card { border: 1px solid var(--border); border-radius: 12px; background: rgba(255,255,255,0.02); }
  .ov-card.open { border-color: rgba(212,175,55,0.4); }
  .ov-row, .ov-rrow { width: 100%; display: flex; align-items: center; gap: 8px; background: transparent; border: none; color: var(--text); font: inherit; text-align: left; cursor: pointer; }
  .ov-row { padding: 12px 14px; }
  .ov-name { flex: 1; min-width: 0; font-size: 14px; font-weight: 600; }
  .ov-body { padding: 0 14px 14px; display: flex; flex-direction: column; gap: 8px; }
  .ov-promise { margin: 0; color: var(--text-muted); font-size: 13px; }
  .ov-route { border-top: 1px solid var(--border); padding-top: 8px; }
  .ov-rrow { padding: 4px 0; }
  .ov-rname { flex: 1; min-width: 0; font-size: 13px; }
  .ov-dim { color: var(--text-dim); font-size: 12px; }
  .ov-open, .ov-go { display: inline-block; margin: 4px 10px 0 0; color: #7ab8ff; font-size: 12px; text-decoration: none; background: none; border: none; padding: 0; font-family: inherit; cursor: pointer; }
  .ov-link { align-self: flex-start; background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; color: var(--text-muted); font: inherit; font-size: 12px; padding: 6px 8px; }
  .chip { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; border: 1px solid; white-space: nowrap; }
  .chip.tech { color: #7ab8ff; border-color: rgba(122,184,255,0.45); }
  .chip.irl { color: #d4af37; border-color: rgba(212,175,55,0.45); }
  .chip.door { color: #c9a6ff; border-color: rgba(187,148,246,0.45); }
  .chip.best { color: #141216; background: #9fd49f; border-color: #9fd49f; }
</style>
